import { NODE_CATALOG } from './catalog.js';
import { findIntegration } from './integrations.js';
/**
 * How much each field is worth. A hit in the title beats a hit in a keyword,
 * which beats a passing mention in the description — otherwise "weight" would
 * rank body fat (whose description mentions a scale) alongside Weight itself.
 */
const FIELD_WEIGHT = {
    title: 10,
    keyword: 8,
    // Above description: someone typing "withings" is naming a service, not
    // hoping a description happens to mention one.
    integration: 7,
    description: 5,
    category: 4,
    unit: 4,
    source: 4,
    kind: 3,
};
/**
 * Vocabulary for the parts of a definition that are enum values rather than
 * prose, so that searching "chart", "device" or "by hand" finds the types
 * those words are true of.
 *
 * Keyed by the enums in `types.ts`, never by a node type's `key` — which is
 * what keeps adding a node type a one-entry change. Adding a new `kind` or
 * `source` is an edit to those enums, and this map goes with them.
 */
const KIND_WORDS = {
    metric: ['metric', 'number', 'measurement', 'chart', 'graph', 'history', 'goal', 'target', 'over time'],
    collection: ['collection', 'records', 'entries', 'set', 'list', 'people'],
    document: ['document', 'writing', 'text', 'long form', 'attachments', 'files'],
};
const SOURCE_WORDS = {
    withings: ['withings', 'device', 'scale', 'automatic', 'sync', 'synced', 'connected'],
    manual: ['manual', 'manually', 'by hand', 'yourself', 'type it in', 'enter'],
    import: ['import', 'imported', 'upload', 'file', 'gedcom'],
};
/**
 * Lowercases, strips accents, and reduces anything that is not a letter or a
 * digit to a space — so "Blood pressure", "blood-pressure" and "BLOODPRESSURE"
 * do not have to be spelled the same way to meet.
 */
function normalize(value) {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
function tokenize(value) {
    const normalized = normalize(value);
    return normalized.length === 0 ? [] : normalized.split(' ');
}
/**
 * Dice coefficient over letter pairs, which is what carries the misspelling
 * tolerance.
 *
 * Preferred over an edit-distance cutoff because of the exact case this guide
 * has to survive: "genelogie" against "genealogy" is three edits on a nine
 * letter word — a cutoff loose enough to allow that is loose enough to allow
 * a great deal else. The two words nonetheless share five of their eight
 * letter pairs, which this scores at 0.63, comfortably clear of a threshold
 * that "height" against "weight" also clears. That last one is a genuine false
 * positive and it is kept on purpose: it ranks below every real match, and a
 * reader who typed "height" is better served by a near miss than by an empty
 * page.
 */
function bigrams(value) {
    const pairs = [];
    for (let i = 0; i < value.length - 1; i += 1)
        pairs.push(value.slice(i, i + 2));
    return pairs;
}
function diceCoefficient(a, b) {
    if (a === b)
        return 1;
    if (a.length < 2 || b.length < 2)
        return 0;
    const aPairs = bigrams(a);
    const remaining = new Map();
    for (const pair of bigrams(b))
        remaining.set(pair, (remaining.get(pair) ?? 0) + 1);
    let shared = 0;
    for (const pair of aPairs) {
        const count = remaining.get(pair) ?? 0;
        if (count > 0) {
            shared += 1;
            remaining.set(pair, count - 1);
        }
    }
    return (2 * shared) / (aPairs.length + bigrams(b).length);
}
/** Below this, two words are different words rather than one misspelled. */
const FUZZY_THRESHOLD = 0.6;
/** Too short to misspell usefully — at three letters everything resembles everything. */
const MIN_FUZZY_LENGTH = 5;
/** Everything about one definition that a query can be matched against. */
function fieldsOf(definition) {
    const fields = [
        { field: 'title', term: definition.title, tokens: tokenize(definition.title) },
        { field: 'description', term: definition.description, tokens: tokenize(definition.description) },
        { field: 'category', term: definition.category, tokens: tokenize(definition.category) },
    ];
    for (const keyword of definition.keywords ?? []) {
        fields.push({ field: 'keyword', term: keyword, tokens: tokenize(keyword) });
    }
    for (const word of KIND_WORDS[definition.kind]) {
        fields.push({ field: 'kind', term: word, tokens: tokenize(word) });
    }
    for (const source of definition.sources) {
        for (const word of SOURCE_WORDS[source] ?? [source]) {
            fields.push({ field: 'source', term: word, tokens: tokenize(word) });
        }
    }
    for (const link of definition.integrations ?? []) {
        const integration = findIntegration(link.provider);
        // An unknown provider key is a typo in the catalog. A test names it; the
        // guide should not also crash a reader's search over it.
        if (!integration)
            continue;
        // The service's own name and key, so "withings" finds everything it feeds
        // — and `provides`, which is written about THIS node type. Deliberately
        // not `summary`: that describes the service and is identical across every
        // type it feeds, so indexing it made "blood pressure" (which the Withings
        // summary mentions) return weight and muscle mass too.
        for (const term of [integration.name, integration.key, link.provides]) {
            fields.push({ field: 'integration', term: integration.name, tokens: tokenize(term) });
        }
    }
    if (definition.metric) {
        const { canonical, display } = definition.metric.unit;
        for (const unit of new Set([canonical, display])) {
            fields.push({ field: 'unit', term: unit, tokens: tokenize(unit) });
        }
    }
    return fields;
}
/** The best way this one query word matches this one definition, if at all. */
function bestHit(queryToken, fields) {
    let best = null;
    const consider = (quality, field, fuzzy) => {
        const score = quality * FIELD_WEIGHT[field.field];
        if (!best || score > best.score) {
            best = { score, field: field.field, term: field.term, fuzzy };
        }
    };
    for (const field of fields) {
        for (const token of field.tokens) {
            if (token === queryToken) {
                consider(1, field, false);
                continue;
            }
            // A reader typing "geneal" is part-way through a word, not misspelling
            // one — worth more than a resemblance, and cheaper to be sure of.
            if (queryToken.length >= 3 && token.startsWith(queryToken)) {
                consider(0.85, field, false);
                continue;
            }
            if (queryToken.length >= 4 && token.includes(queryToken)) {
                consider(0.65, field, false);
                continue;
            }
            if (queryToken.length >= MIN_FUZZY_LENGTH && token.length >= MIN_FUZZY_LENGTH) {
                const similarity = diceCoefficient(queryToken, token);
                if (similarity >= FUZZY_THRESHOLD)
                    consider(0.5 * similarity, field, true);
            }
        }
    }
    return best;
}
/**
 * Ranked node types for a query, best first.
 *
 * An empty query is not a failed search — it is the guide at rest, so it
 * returns the whole catalogue in its own order. Multiple words narrow rather
 * than widen: every word has to match something, so "blood pressure" finds the
 * two blood pressure types and "family tree" finds the tree rather than every
 * type whose description says "family".
 */
export function searchNodeTypes(query, catalog = NODE_CATALOG) {
    const queryTokens = tokenize(query);
    if (queryTokens.length === 0) {
        return catalog.map((definition) => ({
            definition,
            score: 0,
            matchedOn: 'title',
            matchedTerm: null,
            fuzzy: false,
        }));
    }
    const matches = [];
    for (const definition of catalog) {
        const fields = fieldsOf(definition);
        const hits = [];
        for (const queryToken of queryTokens) {
            const hit = bestHit(queryToken, fields);
            // Every word must land somewhere. Without this, a second word could only
            // ever add score, so "weight gedcom" would still rank Weight top.
            if (!hit) {
                hits.length = 0;
                break;
            }
            hits.push(hit);
        }
        if (hits.length === 0)
            continue;
        const best = hits.reduce((a, b) => (b.score > a.score ? b : a));
        matches.push({
            definition,
            score: hits.reduce((total, hit) => total + hit.score, 0),
            matchedOn: best.field,
            matchedTerm: best.field === 'title' ? null : best.term,
            fuzzy: hits.every((hit) => hit.fuzzy),
        });
    }
    // Ties keep catalogue order, which groups the body metrics together rather
    // than shuffling them on every keystroke.
    return matches
        .map((match, index) => ({ match, index }))
        .sort((a, b) => b.match.score - a.match.score || a.index - b.index)
        .map(({ match }) => match);
}
//# sourceMappingURL=search.js.map